import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ProfileFormValues, queryKeys, UserRoles } from '@/types/user';
import {
  getUserInfoService,
  updateAdvisorService,
  updateStaffService,
} from '@/services/user';
import { useAuthUser } from '../stores/useAuthUser';
import { useEffect } from 'react';

export const useGetUser = () => {
  const { setIsStaff } = useAuthUser();
  const { data, isPending, isSuccess } = useQuery({
    queryKey: queryKeys.detail,
    queryFn: getUserInfoService,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  });

  useEffect(() => {
    if (isSuccess) {
      setIsStaff(data?.role === 'staff');
    }
  }, [data, isSuccess]);

  return {
    user: data,
    canSeeTeammates:
      data?.role === UserRoles.STAFF || data?.role === UserRoles.TEAMMATE_ADMIN,
    isPending,
  };
};

export function useUpdateUser() {
  const queryClient = useQueryClient();
  const { mutateAsync: updateStaff, isPending: isUpdateStaffPending } =
    useMutation({
      mutationFn: (data: ProfileFormValues) => updateStaffService(data),
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.detail,
        });
      },
    });

  const { mutateAsync: updateAdvisor, isPending: isUpdateAdvisorPending } =
    useMutation({
      mutationFn: (data: ProfileFormValues) => updateAdvisorService(data),
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.detail,
        });
      },
    });

  return {
    updateStaff,
    updateAdvisor,
    isUpdateStaffPending,
    isUpdateAdvisorPending,
  };
}
